import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pmsKeys } from '../queryKeys';
import { formsApi } from './forms.api';
import type {
  FormAudienceExclusionBulkPayload,
  FormAudienceExclusionPayload,
  CreateFormPayload,
  UpdateFormPayload,
  FormTemplate,
  FormTemplateDetail,
} from '../types';

export function usePmsForms() {
  return useQuery({
    queryKey: pmsKeys.forms(),
    queryFn: () => formsApi.list(),
    select: (d) => (Array.isArray(d) ? d : []) as FormTemplate[],
  });
}

export function usePmsForm(templateId: string | undefined) {
  return useQuery({
    queryKey: pmsKeys.form(templateId ?? ''),
    queryFn: () => formsApi.get(templateId as string),
    enabled: !!templateId,
  });
}

/** Drives the stepper ceiling — refetched after any change that could unblock a step. */
export function usePmsFormGate(templateId: string | undefined) {
  return useQuery({
    queryKey: pmsKeys.formGate(templateId ?? ''),
    queryFn: () => formsApi.gateCheck(templateId as string),
    enabled: !!templateId,
  });
}

/**
 * What publishing WOULD do — the escalation picture, before committing.
 *
 * `staleTime: 0`: it reads the live org chart and the evaluator flags, both of
 * which an admin may be fixing in another tab precisely BECAUSE this screen told
 * them to. A cached answer would show the problem they just solved.
 */
export function usePmsFormRunPreview(templateId: string | undefined) {
  return useQuery({
    queryKey: pmsKeys.formRunPreview(templateId ?? ''),
    queryFn: () => formsApi.runPreview(templateId as string),
    enabled: !!templateId,
    staleTime: 0,
  });
}

/**
 * D-29 — flip one person in the previewed audience to "not invited", or back.
 *
 * Writes the returned preview straight into the cache instead of invalidating:
 * the server already recomputed the whole table for this exact answer, and a
 * refetch would blank the row the admin just clicked. The gate is invalidated
 * too — excluding the last person in an audience blocks publish.
 */
export function useSetPmsAudienceExclusion(templateId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      membershipId,
      ...data
    }: FormAudienceExclusionPayload & { membershipId: string }) =>
      formsApi.setAudienceExclusion(templateId as string, membershipId, data),
    onSuccess: (preview) => {
      qc.setQueryData(pmsKeys.formRunPreview(templateId ?? ''), preview);
      qc.invalidateQueries({ queryKey: pmsKeys.formGate(templateId ?? '') });
    },
  });
}

/** D-29 — the bulk bar behind that same table. Same cache contract as above. */
export function useSetPmsAudienceExclusions(templateId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: FormAudienceExclusionBulkPayload) =>
      formsApi.setAudienceExclusions(templateId as string, data),
    onSuccess: (preview) => {
      qc.setQueryData(pmsKeys.formRunPreview(templateId ?? ''), preview);
      qc.invalidateQueries({ queryKey: pmsKeys.formGate(templateId ?? '') });
    },
  });
}

export function useCreatePmsForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFormPayload) => formsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: pmsKeys.forms() }),
  });
}

export function useUpdatePmsForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: UpdateFormPayload }) =>
      formsApi.update(templateId, data),
    onSuccess: (_r, { templateId }) => {
      qc.invalidateQueries({ queryKey: pmsKeys.form(templateId) });
      // The register shows type/period/updated, and the gate depends on the
      // designation — both go stale on any template edit.
      qc.invalidateQueries({ queryKey: pmsKeys.forms() });
      qc.invalidateQueries({ queryKey: pmsKeys.formGate(templateId) });
    },
  });
}

export function usePublishPmsForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => formsApi.publish(templateId),
    onSuccess: (_r, templateId) => {
      qc.invalidateQueries({ queryKey: pmsKeys.forms() });
      qc.invalidateQueries({ queryKey: pmsKeys.form(templateId) });
    },
  });
}

export function useDeletePmsForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => formsApi.delete(templateId),
    onSuccess: () => qc.invalidateQueries({ queryKey: pmsKeys.forms() }),
  });
}

/**
 * Step 1's "Use existing" — apply a source form's configuration to this draft.
 *
 * Invalidates the form itself AND its questions: the copy replaces sections and
 * questions wholesale, so a stale questions cache would show step 2 still
 * holding the draft's previous content.
 */
export function useReusePmsForm() {
  const qc = useQueryClient();
  return useMutation<
    FormTemplateDetail,
    Error,
    { templateId: string; sourceTemplateId: string }
  >({
    mutationFn: ({ templateId, sourceTemplateId }) => formsApi.reuse(templateId, sourceTemplateId),
    onSuccess: (_r, { templateId }) => {
      qc.invalidateQueries({ queryKey: pmsKeys.form(templateId) });
      qc.invalidateQueries({ queryKey: pmsKeys.formQuestions(templateId) });
      qc.invalidateQueries({ queryKey: pmsKeys.formGate(templateId) });
      qc.invalidateQueries({ queryKey: pmsKeys.forms() });
    },
  });
}

/**
 * Published versions, newest first.
 *
 * Only fetched for a PUBLISHED form: a Draft has no history to read, and the
 * request would be a guaranteed empty array on every keystroke in step 1.
 */
export function usePmsFormVersions(templateId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...pmsKeys.form(templateId ?? ''), 'versions'],
    queryFn: () => formsApi.versions(templateId as string),
    enabled: !!templateId && enabled,
  });
}
